import { useCallback, useState } from 'react';
import { addMonths } from 'date-fns';
import { Products } from 'enums';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import { User } from '../account-management.types';
import { License } from 'types/licenses.types';

/**
 * Manages all license-related dialog states in account management
 */

interface LinkedinAccountPreviewDialogState {
  open: boolean;
  loading: boolean;
  linkedinAccounts: LinkedinAccount[];
}

interface ManageLicenseUsersDialogState {
  open: boolean;
  loading: boolean;
  stripeProduct: Products | null;
  license: License | null;
  licenseUsers: string[];
  allUsers: User[];
  allUsersLoading: boolean;
}

interface CancelLicenseDialogState {
  open: boolean;
  loading: boolean;
  licenseId: string | null;
}

interface AdminLicenseDialogState {
  open: boolean;
  loading: boolean;
  license: License | null;
  companyId: string | null;
  product: string;
  billingType: 'monthly' | 'quarterly' | 'yearly' | 'one_time';
  credits: number;
  expirationDate: Date;
  products: string[];
}

interface DeleteLicenseDialogState {
  open: boolean;
  loading: boolean;
  license: License | null;
}

const initialLinkedinAccountPreviewDialogState: LinkedinAccountPreviewDialogState = {
  open: false,
  loading: false,
  linkedinAccounts: [],
};

const initialManageLicenseUsersDialogState: ManageLicenseUsersDialogState = {
  open: false,
  loading: false,
  stripeProduct: null,
  license: null,
  licenseUsers: [],
  allUsers: [],
  allUsersLoading: false,
};

const initialCancelLicenseDialogState: CancelLicenseDialogState = {
  open: false,
  loading: false,
  licenseId: null,
};

const initialAdminLicenseDialogState: AdminLicenseDialogState = {
  open: false,
  loading: false,
  license: null,
  companyId: null,
  product: '',
  billingType: 'yearly',
  credits: 0,
  expirationDate: addMonths(new Date(), 1),
  products: [],
};

const initialDeleteLicenseDialogState: DeleteLicenseDialogState = {
  open: false,
  loading: false,
  license: null,
};

export function useLicenseDialogs() {
  const [linkedinAccountPreviewDialog, setLinkedinAccountPreviewDialog] =
    useState<LinkedinAccountPreviewDialogState>(initialLinkedinAccountPreviewDialogState);
  const [manageLicenseUsersDialog, setManageLicenseUsersDialog] = useState<ManageLicenseUsersDialogState>(initialManageLicenseUsersDialogState);
  const [cancelLicenseDialog, setCancelLicenseDialog] = useState<CancelLicenseDialogState>(initialCancelLicenseDialogState);
  const [adminLicenseDialog, setAdminLicenseDialog] = useState<AdminLicenseDialogState>(initialAdminLicenseDialogState);
  const [deleteLicenseDialog, setDeleteLicenseDialog] = useState<DeleteLicenseDialogState>(initialDeleteLicenseDialogState);

  // Reset handlers
  const resetLinkedinAccountPreviewDialog = useCallback(() => {
    setLinkedinAccountPreviewDialog(initialLinkedinAccountPreviewDialogState);
  }, []);

  const resetManageLicenseUsersDialog = useCallback(() => {
    setManageLicenseUsersDialog(initialManageLicenseUsersDialogState);
  }, []);

  const resetCancelLicenseDialog = useCallback(() => {
    setCancelLicenseDialog(initialCancelLicenseDialogState);
  }, []);

  const resetAdminLicenseDialog = useCallback(() => {
    // Reset with fresh date
    setAdminLicenseDialog({
      ...initialAdminLicenseDialogState,
      expirationDate: addMonths(new Date(), 1),
    });
  }, []);

  const resetDeleteLicenseDialog = useCallback(() => {
    setDeleteLicenseDialog(initialDeleteLicenseDialogState);
  }, []);

  return {
    // States and setters
    linkedinAccountPreviewDialog,
    setLinkedinAccountPreviewDialog,
    resetLinkedinAccountPreviewDialog,

    manageLicenseUsersDialog,
    setManageLicenseUsersDialog,
    resetManageLicenseUsersDialog,

    cancelLicenseDialog,
    setCancelLicenseDialog,
    resetCancelLicenseDialog,

    adminLicenseDialog,
    setAdminLicenseDialog,
    resetAdminLicenseDialog,

    deleteLicenseDialog,
    setDeleteLicenseDialog,
    resetDeleteLicenseDialog,
  };
}
